import {
	useState,
	useContext,
	useMemo,
	useEffect,
	useCallback,
	lazy,
} from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';

import config from '@/config';
import { Article, Issue as IssueModel } from '@/constants';

import { useTrackingPageView } from '@/tracking/Tracking.hooks';
import { useFavorites } from '@pages/favorite/Favorite.hooks';

const LazyAudioPlayer = lazy(
	() => import('@widgets/player/audio/AudioPlayer'),
);

const LazyResponsive = lazy(
	() => import('@widgets/issue-reader/responsive/Responsive'),
);
const LazyReplica = lazy(() => import('@widgets/issue-reader/replica/Replica'));

import {
	useDownloadAssets,
	useArticle,
	useLoad,
	useTitle,
	useFont,
	IssueSearch,
	useArticleLang,
	useStylesMap,
} from './Issue.hooks';
import { IssueContext } from '@entities/issue';
import { ReaderStateContext } from '@widgets/issue-reader/model/ReaderStateContext';

import { Loading } from './Loading';
import { AppContext } from '@/App.context';

export const Issue = ({ onClickLogOut }: IssueViewProps) => {
	const { lang, baseUrl } = useContext(AppContext);
	const navigate = useNavigate();
	const router = useRouter();
	const [language, setLanguage] = useState<string>(lang);
	const [mode, setMode] = useState<ContentType>(getDefaultMode());
	const { fontSize, setFontSize } = useFont();
	const { favorites, setFavorites } = useFavorites();
	const [audioPlayerUrl, setAudioPlayerUrl] = useState<string | null>(null);
	const [showAudioPlayer, setShowAudioPlayer] = useState(false);

	const {
		issue,
		isLoading,
		isUnavailableOffline,
		error,
		currentArticle,
		setCurrentArticle,
	} = useLoad();
	const languages = useLanguages(issue);
	const articleLanguages = useArticleLanguages(currentArticle);
	const disabledLanguages = diffSet(languages, articleLanguages);

	// Offline with nothing cached for this issue (useLoad/useIssue determine
	// this synchronously, before ever attempting to fetch): rather than
	// showing anything on this route, bounce back and tell the user why.
	// Prefer going back to wherever they came from (router history) over
	// always landing on home; only fall back to home when there's nowhere
	// to go back to (e.g. a deep link opened directly, offline, on a never-
	// visited issue). navigate()/alert() are side effects, so they belong
	// in an effect, not the render body -- the dependency array means this
	// only fires once per transition into the unavailable state, not on
	// every re-render while it stays true.
	useEffect(() => {
		if (!isUnavailableOffline) return;
		alert('This issue is not available offline.');
		if (router.history.canGoBack()) {
			router.history.back();
		} else {
			navigate({ to: baseUrl, replace: true });
		}
	}, [isUnavailableOffline, navigate, baseUrl, router]);

	useEffect(() => {
		localStorage.setItem('content-type', mode);
	}, [mode]);

	useDownloadAssets({ issue: issue ?? null });

	useArticle({ article: currentArticle ?? null, issue });
	useArticleLang({
		article: currentArticle,
		isLoading,
		languages,
		setLanguage,
	});

	useTitle({ article: currentArticle, lang: language });
	useTrackingPageView({
		enabled: !!currentArticle && !isLoading && !error,
		data: {
			article: currentArticle,
			issue,
			language,
		},
	});

	const stylesMap = useStylesMap(issue);
	const articlesMap: Map<string, Article> | undefined = useMemo(() => {
		if (!issue) {
			return undefined;
		}

		if (!issue.articles) {
			return undefined;
		}
		return issue.articles.reduce((acc: Map<string, Article>, article: Article) => {
			const { id } = article;
			acc.set(`${id}`, article);
			return acc;
		}, new Map<string, Article>());
	}, [issue]);

	const internalArticlesMap: Map<number, Article> = useMemo(() => {
		if (!issue) {
			return new Map();
		}

		if (!issue.articles) {
			return new Map();
		}
		const { articles } = issue;
		return articles.reduce(
			(acc: Map<number, Article>, article: Article) => {
				const { ArticleID } = article;
				acc.set(parseInt(`${ArticleID}`, 10), article);
				return acc;
			},
			new Map(),
		);
	}, [issue]);

	const navigateToArticle = useCallback(
		(articleId: number) => {
			const selectedArticle = internalArticlesMap.get(articleId);
			if (!selectedArticle) {
				return;
			}
			setCurrentArticle(selectedArticle);
		},
		[internalArticlesMap, setCurrentArticle],
	);

	// The effect above is already navigating away in this case -- just show
	// the loading state for the brief moment until that completes, rather
	// than rendering content for a route we're about to leave.
	if (isUnavailableOffline) {
		return <Loading />;
	}

	if (isLoading || !currentArticle || !stylesMap || !issue) {
		return <Loading />;
	}

	return (
		<IssueContext.Provider
			value={{
				language,
				issue,
				stylesMap,
				currentArticle,
				setCurrentArticle,
				articles: issue?.articles ? issue.articles : [],
				articlesMap,
				navigateToArticle,
				languages,
				articleLanguages,
				disabledLanguages,
				setLanguage: (lang) => {
					navigate({
						to: '.',
						search: (prevSearch: IssueSearch) => ({
							...prevSearch,
							lang,
						}),
					});
				},
			}}
		>
			<ReaderStateContext.Provider
				value={{
					setMode,
					fontSize,
					setFontSize,
					favorites,
					setFavorites,
					onClickLogOut,
					audioPlayerUrl,
					setAudioPlayerUrl,
					showAudioPlayer,
					setShowAudioPlayer,
				}}
			>
				<HeadHelmet />
				<LazyAudioPlayer
					url={audioPlayerUrl}
					onClose={() => {
						setAudioPlayerUrl(null);
					}}
				>
					{mode === 'responsive' ? <LazyResponsive /> : <LazyReplica />}
				</LazyAudioPlayer>
			</ReaderStateContext.Provider>
		</IssueContext.Provider>
	);
};

export interface IssueViewProps {
	onClickLogOut?: (url?: string) => void;
}

function HeadHelmet() {
	const { currentArticle, language, issue } = useContext(IssueContext);
	const { role, name, thumbnail } = currentArticle ?? {};
	const keywords =
		currentArticle?.keywords && currentArticle.keywords.length
			? currentArticle.keywords
			: [];
	const keywordsString = [...new Set(keywords)].join(',');
	return (
		<Helmet>
			{issue?.id && <meta property="cf:issue" content={`${issue.id}`} />}
			{currentArticle?.id && (
				<meta property="cf:article" content={`${currentArticle.id}`} />
			)}
			<meta property="og:type" content={role} />
			{name?.[language] ? (
				<meta property="og:title" content={name?.[language]} />
			) : null}
			<meta property="og:url" content={window.location.href} />
			{thumbnail ? (
				<meta property="og:image" content={thumbnail} />
			) : null}
			<meta property="og:category" content="digital-editions" />
			{keywordsString.length ? (
				<meta name="keywords" content={keywordsString}></meta>
			) : null}
			{keywordsString.length ? (
				<meta name="post-tags" content={keywordsString}></meta>
			) : null}
		</Helmet>
	);
}

function getDefaultMode(): ContentType {
	const { contentType } = config.issue;
	switch (contentType) {
		case 'replica':
		case 'responsive':
			return contentType;
		default:
			if (!localStorage.getItem('content-type')) {
				localStorage.setItem('content-type', 'responsive');
			}
			return localStorage.getItem('content-type') as ContentType;
	}
}

type ContentType = 'replica' | 'responsive' | 'responsive-replica';

function useLanguages(issue: IssueModel | null | undefined): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!issue) {
			return;
		}

		if (!issue.articles || !issue.articles.length) {
			return;
		}

		const acc = new Set<string>();
		for (const article of issue.articles) {
			for (const lang in article.name) {
				acc.add(lang);
			}
		}
		setLanguages(acc);
	}, [issue]);

	return languages;
}

function useArticleLanguages(article: Article | null | undefined): Set<string> {
	const [languages, setLanguages] = useState(new Set<string>());
	useEffect(() => {
		if (!article) {
			return;
		}

		const acc = new Set<string>();
		for (const lang in article.name) {
			acc.add(lang);
		}

		setLanguages(acc);
	}, [article]);
	return languages;
}

function diffSet<T>(a: Set<T>, b: Set<T>): Set<T> {
	const diff = [...a].filter((i) => !b.has(i));
	return new Set(diff);
}

export default Issue;
